import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { triggerFormSubmissionAutomations } from '../../lib/automationsV2';
import { findExistingCustomer, updateCustomerFromBooking } from '../../lib/customers';
import { createLeadForCustomer, emitLeadCreated } from '../../lib/crm/createLead';
import { getLeadFormPublicSlug, leadFormTitleToSlug } from '../../lib/crm/leadForms';
import { CheckSquare, Phone, Mail, MapPin } from 'lucide-react';

interface Business {
  id: string;
  name: string;
  slug: string;
  phone?: string;
  email?: string;
  address?: string;
  booking_settings?: {
    logo_url?: string;
    navbar_color?: string;
  };
}

interface LeadForm {
  id: string;
  business_id: string;
  title: string;
  description?: string;
  public_slug?: string | null;
  form_config?: {
    fields: Array<{
      id: string;
      type: string;
      label: string;
      mandatory: boolean;
      isCustom?: boolean;
      options?: Array<{id: string, label: string}>;
      placeholder?: string;
    }>;
    sub_header?: string;
    header_color?: string;
    logo_url?: string;
    background_color?: string;
    text_color?: string;
    button_color?: string;
    show_business_name?: boolean;
    show_phone?: boolean;
    show_email?: boolean;
    show_address?: boolean;
    show_direct_contact?: boolean;
    redirect_url?: string;
    button_text?: string;
  };
  is_active: boolean;
}

export default function PublicLeadForm() {
  const { businessSlug, formSlug } = useParams<{ businessSlug: string; formSlug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEmbed = searchParams.get('embed') === '1';
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [business, setBusiness] = useState<Business | null>(null);
  const [leadForm, setLeadForm] = useState<LeadForm | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    async function loadFormData() {
      if (!businessSlug || !formSlug) {
        setError('Invalid URL');
        setIsLoading(false);
        return;
      }

      if (!supabase) {
        setError('Unable to connect to the database');
        setIsLoading(false);
        return;
      }

      try {
        // First, get the business by slug
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('id, name, slug, phone, email, address, booking_settings')
          .eq('slug', businessSlug)
          .single();

        if (businessError || !businessData) {
          setError('Business not found');
          setIsLoading(false);
          return;
        }

        setBusiness(businessData);

        console.log('Looking for form with slug:', formSlug);
        
        // Get all lead forms for this business to debug
        const { data: allForms, error: allFormsError } = await supabase
          .from('lead_forms')
          .select('*')
          .eq('business_id', businessData.id)
          .eq('is_active', true);

        console.log('All forms for business:', allForms);

        if (allFormsError) {
          console.error('Error fetching forms:', allFormsError);
          setError('Error fetching forms');
          setIsLoading(false);
          return;
        }

        if (!allForms || allForms.length === 0) {
          setError('No active lead forms found for this business');
          setIsLoading(false);
          return;
        }

        // Try multiple matching strategies
        let matchedForm = null;

        // Strategy 1: stored public_slug (custom URL)
        matchedForm = allForms.find(
          (form) => getLeadFormPublicSlug(form) === formSlug,
        );

        // Strategy 2: Convert slug to title (replace hyphens with spaces and capitalize)
        if (!matchedForm) {
          const formTitle = formSlug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
          matchedForm = allForms.find((form) => form.title === formTitle);
        }

        // Strategy 3: Case-insensitive title matching
        if (!matchedForm) {
          const formTitle = formSlug.replace(/-/g, ' ');
          matchedForm = allForms.find(
            (form) => form.title.toLowerCase() === formTitle.toLowerCase(),
          );
        }

        // Strategy 4: title-derived slug
        if (!matchedForm) {
          matchedForm = allForms.find((form) => leadFormTitleToSlug(form.title) === formSlug);
        }

        console.log('Matched form:', matchedForm);

        if (!matchedForm) {
          const availableForms = allForms
            .map(
              (f) =>
                `"${f.title}" -> /${businessSlug}/${getLeadFormPublicSlug(f)}`,
            )
            .join(', ');
          setError(`Form not found. Available forms: ${availableForms}`);
          setIsLoading(false);
          return;
        }

        setLeadForm(matchedForm);
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading form data:', err);
        setError('Failed to load form');
        setIsLoading(false);
      }
    }

    loadFormData();
  }, [businessSlug, formSlug]);

  const handleInputChange = (fieldId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleCheckboxChange = (fieldId: string, optionId: string, checked: boolean) => {
    setFormData(prev => {
      const currentValues = prev[fieldId] || [];
      if (checked) {
        return {
          ...prev,
          [fieldId]: [...currentValues, optionId]
        };
      } else {
        return {
          ...prev,
          [fieldId]: currentValues.filter((id: string) => id !== optionId)
        };
      }
    });
  };

  const validateForm = () => {
    if (!leadForm?.form_config?.fields) return true;

    for (const field of leadForm.form_config.fields) {
      if (field.mandatory) {
        if (field.type === 'customer_info') {
          // For customer_info fields, check all sub-fields
          const firstName = formData[`${field.id}_first_name`];
          const lastName = formData[`${field.id}_last_name`];
          const email = formData[`${field.id}_email`];
          const phone = formData[`${field.id}_phone`];
          
          if (!firstName?.trim() || !lastName?.trim() || !email?.trim() || !phone?.trim()) {
            return false;
          }
        } else {
          // For other fields, check normally
          const value = formData[field.id];
          if (!value || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && value.trim() === '')) {
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      alert('Please fill out all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (!supabase) {
        throw new Error('Database connection not available');
      }

      // Extract customer information from form data
      // Check if form has customer_info component, otherwise fallback to individual fields
      const customerInfoField = leadForm?.form_config?.fields?.find(field => field.type === 'customer_info');
      
      let customerData;
      if (customerInfoField) {
        // Extract from customer_info component with standardized field names
        customerData = {
          first_name: formData[`${customerInfoField.id}_first_name`] || '',
          last_name: formData[`${customerInfoField.id}_last_name`] || '',
          email: formData[`${customerInfoField.id}_email`] || '',
          phone: formData[`${customerInfoField.id}_phone`] || ''
        };
        console.log('📋 LEAD FORM: Found customer_info component, extracting standardized fields:', customerData);
      } else {
        // Fallback to old method for forms without customer_info component
        customerData = {
          first_name: formData['first_name'] || formData['firstName'] || '',
          last_name: formData['last_name'] || formData['lastName'] || '',
          email: formData['email'] || '',
          phone: formData['phone'] || formData['phone_number'] || ''
        };
        console.log('📋 LEAD FORM: No customer_info component found, using fallback field extraction:', customerData);
      }

      console.log('👤 LEAD FORM: Extracted customer data:', customerData);

      // Create or find customer using the same logic as online booking
      let customerId: string | null = null;

      if (customerData.email || customerData.phone) {
        console.log('🔍 LEAD FORM: Looking for existing customer with:', {
          business_id: business?.id,
          email: customerData.email,
          phone: customerData.phone
        });

        const existingCustomer = await findExistingCustomer(
          business?.id || '',
          customerData.email,
          customerData.phone
        );

        if (existingCustomer) {
          console.log('👤 LEAD FORM: Found existing customer:', {
            id: existingCustomer.id,
            first_name: existingCustomer.first_name,
            last_name: existingCustomer.last_name,
            email: existingCustomer.email,
            phone: existingCustomer.phone
          });
          
          // Update the existing customer with any new information (same as online booking)
          const updatedCustomer = await updateCustomerFromBooking(
            existingCustomer.id,
            {
              first_name: customerData.first_name,
              last_name: customerData.last_name,
              email: customerData.email,
              phone: customerData.phone
            }
          );
          
          customerId = existingCustomer.id;
          console.log('✅ LEAD FORM: Updated existing customer:', updatedCustomer);
        } else {
          // Create a new customer (same structure as online booking)
          console.log('🆕 LEAD FORM: No matching customer found. Creating new customer');
          const { data: newCustomer, error: customerError } = await supabase
            .from('customers')
            .insert({
              business_id: business?.id,
              first_name: customerData.first_name,
              last_name: customerData.last_name,
              email: customerData.email,
              phone: customerData.phone,
              source: 'Lead Form',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .select('id')
            .single();

          if (customerError) {
            console.error('❌ LEAD FORM: Error creating customer:', customerError);
            throw new Error(`Failed to create customer: ${customerError.message}`);
          }
          
          if (!newCustomer?.id) {
            throw new Error('Failed to create customer record');
          }
          
          customerId = newCustomer.id;
          console.log('✨ LEAD FORM: Created new customer:', customerId);
        }
      } else {
        console.log('⚠️ LEAD FORM: No email or phone provided, skipping customer creation');
      }

      // Create lead form submission with customer reference
      const submissionData: any = {
        business_id: business?.id,
        lead_form_id: leadForm?.id,
        form_data: formData,
        submitted_at: new Date().toISOString()
      };

      // Add customer_id if we created/found a customer
      if (customerId) {
        submissionData.customer_id = customerId;
        console.log('🔗 LEAD FORM: Linking submission to customer:', customerId);
      }

      // Convert checkbox option IDs to display text before saving
      const processedFormData = { ...formData };
      
      // Helper function to generate clean field IDs from labels (matches LeadForms.tsx)
      const generateFieldId = (label: string): string => {
        return label
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '') // Remove special characters
          .replace(/\s+/g, '_')        // Replace spaces with underscores
          .replace(/_+/g, '_')         // Remove duplicate underscores
          .replace(/^_|_$/g, '');      // Remove leading/trailing underscores
      };

      // Normalize field IDs: Convert from timestamps to clean names for variable mapping
      const normalizedFormData: any = {};
      leadForm?.form_config?.fields?.forEach(field => {
        const cleanFieldId = generateFieldId(field.label);
        
        if (field.type === 'customer_info') {
          // Handle customer info fields specially
          const firstNameKey = `${field.id}_first_name`;
          const lastNameKey = `${field.id}_last_name`;
          const emailKey = `${field.id}_email`;
          const phoneKey = `${field.id}_phone`;
          
          if (processedFormData[firstNameKey]) normalizedFormData['first_name'] = processedFormData[firstNameKey];
          if (processedFormData[lastNameKey]) normalizedFormData['last_name'] = processedFormData[lastNameKey];
          if (processedFormData[emailKey]) normalizedFormData['email'] = processedFormData[emailKey];
          if (processedFormData[phoneKey]) normalizedFormData['phone'] = processedFormData[phoneKey];
        } else {
          // Handle regular fields
          if (processedFormData[field.id] !== undefined) {
            normalizedFormData[cleanFieldId] = processedFormData[field.id];
            console.log(`🔄 FIELD MAPPING: "${field.label}" ${field.id} → ${cleanFieldId}`, processedFormData[field.id]);
          }
        }
      });
      
      // Merge normalized data back into processedFormData for database storage and automation
      Object.assign(processedFormData, normalizedFormData);
      
      // Process checkbox fields to convert option IDs to labels
      leadForm?.form_config?.fields?.forEach(field => {
        if (field.type === 'checkbox' && field.options && processedFormData[field.id]) {
          const selectedOptionIds = processedFormData[field.id];
          if (Array.isArray(selectedOptionIds)) {
            // Convert option IDs to their labels
            const selectedLabels = selectedOptionIds
              .map(optionId => {
                const option = field.options?.find(opt => opt.id === optionId);
                return option ? option.label : optionId; // Fallback to ID if label not found
              })
              .filter(label => label); // Remove any undefined values
            
            // Store as comma-separated string for both original and clean field IDs
            const checkboxValue = selectedLabels.join(', ');
            processedFormData[field.id] = checkboxValue; // Keep original for database
            
            // Also store under clean field ID for variable mapping (already done above, but update it)
            const cleanFieldId = generateFieldId(field.label);
            processedFormData[cleanFieldId] = checkboxValue;
            
            console.log(`✅ CHECKBOX: Converted ${field.label}: [${selectedOptionIds.join(', ')}] → "${checkboxValue}"`);
            console.log(`🔄 CHECKBOX MAPPING: ${field.id} → ${cleanFieldId}`);
          }
        }
      });

      // Update the submission data with processed form data
      submissionData.form_data = processedFormData;

      // Build CRM lead_info from non-customer form fields (Label: value)
      const leadInfoLines: string[] = [];
      leadForm?.form_config?.fields?.forEach((field) => {
        if (field.type === 'customer_info') return;
        const raw = processedFormData[field.id];
        if (raw === undefined || raw === null || raw === '') return;
        const value = Array.isArray(raw) ? raw.join(', ') : String(raw).trim();
        if (!value) return;
        leadInfoLines.push(`${field.label}: ${value}`);
      });
      const leadInfo = leadInfoLines.join('\n') || null;

      const { error: submissionError } = await supabase
        .from('lead_form_submissions')
        .insert(submissionData);

      if (submissionError) {
        console.error('❌ LEAD FORM: Error creating submission:', submissionError);
        throw submissionError;
      }

      console.log('✅ LEAD FORM: Created form submission successfully');

      // Auto-create CRM opportunity (no automation required)
      if (business?.id && customerId) {
        try {
          await createLeadForCustomer(
            business.id,
            {
              id: customerId,
              first_name: customerData.first_name || '',
              last_name: customerData.last_name || '',
            },
            {
              source: leadForm?.title || 'Lead Form',
              lead_info: leadInfo,
            },
          );
          emitLeadCreated(customerId);
          console.log('✅ LEAD FORM: Created CRM opportunity for customer:', customerId);
        } catch (crmError) {
          console.error('❌ LEAD FORM: Failed to create CRM opportunity:', crmError);
          // Don't block thank-you / redirect — submission already saved
        }
      }

      // Trigger form submission automations (non-blocking)
      if (business?.id && leadForm?.id) {
        try {
          console.log('🎯 FORM SUBMISSION: Triggering form submission automations');
          console.log('🎯 FORM SUBMISSION: Business ID:', business.id);
          console.log('🎯 FORM SUBMISSION: Form ID:', leadForm.id);
          console.log('🎯 FORM SUBMISSION: Form data:', formData);
          
          // Prepare enhanced form data with additional context
          const enhancedFormData = {
            ...processedFormData, // Use processed form data with converted checkbox labels
            business_name: business.name,
            form_title: leadForm.title,
            submitted_at: new Date().toISOString(),
            business_email: business.email,
            business_phone: business.phone,
            customer_id: customerId // Pass the customer ID to automation (may be null if no customer info provided)
          };
          
          // Log customer ID status for debugging
          if (customerId) {
            console.log('🎯 FORM SUBMISSION: Customer ID available for automation:', customerId);
          } else {
            console.log('⚠️ FORM SUBMISSION: No customer ID available - automations may skip CRM lead creation');
          }
          
          triggerFormSubmissionAutomations(business.id, leadForm.id, enhancedFormData)
            .then(() => console.log('🤖 FORM SUBMISSION: Automations completed successfully'))
            .catch(automationError => console.error('❌ FORM SUBMISSION: Error triggering automations:', automationError));
          
          console.log('🤖 FORM SUBMISSION: Automation trigger initiated');
        } catch (automationError) {
          console.error('❌ FORM SUBMISSION: Failed to trigger automations:', automationError);
          // We don't throw here to avoid blocking the form submission flow
        }
      }

      // Check if there's a redirect URL configured
      if (leadForm?.form_config?.redirect_url) {
        console.log('🔗 FORM SUBMISSION: Redirecting to:', leadForm.form_config.redirect_url);
        window.location.href = leadForm.form_config.redirect_url;
      } else {
        setIsSubmitted(true);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      alert('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field: any) => {
    const textColor = leadForm?.form_config?.text_color || '#374151';
    const accentColor = leadForm?.form_config?.button_color || '#2563eb';
    
    switch (field.type) {
      case 'customer_info':
        return (
          <div key={field.id} className="mb-8">
            <label 
              className="block text-lg font-semibold mb-4"
              style={{ color: textColor }}
            >
              Your Information
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label 
                    htmlFor={`${field.id}_first_name`}
                    className="block text-sm font-medium mb-2"
                    style={{ color: textColor }}
                  >
                    First Name *
                  </label>
                  <input
                    type="text"
                    id={`${field.id}_first_name`}
                    value={formData[`${field.id}_first_name`] || ''}
                    onChange={(e) => handleInputChange(`${field.id}_first_name`, e.target.value)}
                    placeholder="Enter first name..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 text-base bg-white"
                    style={{
                      '--tw-ring-color': accentColor
                    } as React.CSSProperties}
                    required
                  />
                </div>
                <div>
                  <label 
                    htmlFor={`${field.id}_last_name`}
                    className="block text-sm font-medium mb-2"
                    style={{ color: textColor }}
                  >
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id={`${field.id}_last_name`}
                    value={formData[`${field.id}_last_name`] || ''}
                    onChange={(e) => handleInputChange(`${field.id}_last_name`, e.target.value)}
                    placeholder="Enter last name..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 text-base bg-white"
                    style={{
                      '--tw-ring-color': accentColor
                    } as React.CSSProperties}
                    required
                  />
                </div>
              </div>
              <div>
                <label 
                  htmlFor={`${field.id}_email`}
                  className="block text-sm font-medium mb-2"
                  style={{ color: textColor }}
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id={`${field.id}_email`}
                  value={formData[`${field.id}_email`] || ''}
                  onChange={(e) => handleInputChange(`${field.id}_email`, e.target.value)}
                  placeholder="Enter email address..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 text-base bg-white"
                  style={{
                    '--tw-ring-color': accentColor
                  } as React.CSSProperties}
                  required
                />
              </div>
              <div>
                <label 
                  htmlFor={`${field.id}_phone`}
                  className="block text-sm font-medium mb-2"
                  style={{ color: textColor }}
                >
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id={`${field.id}_phone`}
                  value={formData[`${field.id}_phone`] || ''}
                  onChange={(e) => handleInputChange(`${field.id}_phone`, e.target.value)}
                  placeholder="Enter phone number..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 text-base bg-white"
                  style={{
                    '--tw-ring-color': accentColor
                  } as React.CSSProperties}
                  required
                />
              </div>
            </div>
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.id} className="mb-8">
            <label 
              className="block text-base font-medium mb-3"
              style={{ color: textColor }}
            >
              {field.label}
              {field.mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {field.options?.map((option: any) => (
                <div key={option.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`${field.id}-${option.id}`}
                    checked={(formData[field.id] || []).includes(option.id)}
                    onChange={(e) => handleCheckboxChange(field.id, option.id, e.target.checked)}
                    className="rounded border-gray-300 focus:ring-2"
                    style={{ 
                      accentColor: accentColor,
                      '--tw-ring-color': accentColor 
                    } as React.CSSProperties}
                  />
                  <label 
                    htmlFor={`${field.id}-${option.id}`} 
                    className="ml-2 text-sm"
                    style={{ color: textColor }}
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className="mb-8">
            <label 
              htmlFor={field.id} 
              className="block text-base font-medium mb-3"
              style={{ color: textColor }}
            >
              {field.label}
              {field.mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              id={field.id}
              value={formData[field.id] || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 h-28 resize-none text-base"
              style={{
                borderColor: '#d1d5db',
                '--tw-ring-color': accentColor
              } as React.CSSProperties}
              required={field.mandatory}
            />
          </div>
        );

      default:
        return (
          <div key={field.id} className="mb-8">
            <label 
              htmlFor={field.id} 
              className="block text-base font-medium mb-3"
              style={{ color: textColor }}
            >
              {field.label}
              {field.mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type={field.type}
              id={field.id}
              value={formData[field.id] || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              placeholder={
                field.placeholder || (
                  field.type === 'email' ? 'Enter email address...' :
                  field.type === 'tel' ? 'Enter phone number...' :
                  field.type === 'number' ? 'Enter number...' :
                  field.type === 'url' ? 'Enter URL...' :
                  field.type === 'date' ? 'Select date...' :
                  `Enter ${field.label.toLowerCase()}...`
                )
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 text-base"
              style={{
                borderColor: '#d1d5db',
                '--tw-ring-color': accentColor
              } as React.CSSProperties}
              required={field.mandatory}
            />
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error || !business || !leadForm) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg">{error || 'Form not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div 
        className={isEmbed ? 'min-h-0' : 'min-h-screen'}
        style={{ backgroundColor: leadForm?.form_config?.background_color || (isEmbed ? '#ffffff' : '#f9fafb') }}
      >
        {/* Header */}
        {!isEmbed ? (
        <div 
          className="h-24 w-full flex items-center relative px-6 sm:px-8" 
          style={{ backgroundColor: leadForm?.form_config?.header_color || business?.booking_settings?.navbar_color || '#000000' }}
        >
          {/* Logo - Left Side */}
          {(leadForm?.form_config?.logo_url || business?.booking_settings?.logo_url) && (
            <div className="flex-shrink-0">
              <img 
                src={leadForm?.form_config?.logo_url || business?.booking_settings?.logo_url!} 
                alt={`${business.name} logo`}
                className="h-16 max-w-xs object-contain"
              />
            </div>
          )}
          
          {/* Business Name - Centered */}
          {leadForm?.form_config?.show_business_name !== false && (
            <div className="absolute inset-0 flex items-center justify-center">
              <h1 
                className="text-3xl font-bold"
                style={{ color: '#ffffff' }}
              >
                {business.name}
              </h1>
            </div>
          )}
        </div>
        ) : null}

        {/* Success Message */}
        <div className={`${isEmbed ? 'max-w-xl mx-auto px-3 py-6' : 'max-w-6xl mx-auto px-4 py-12'}`}>
          <div 
            className={`${isEmbed ? '' : 'bg-white rounded-lg shadow-md overflow-hidden'}`}
            style={{ 
              backgroundColor: leadForm?.form_config?.background_color || '#ffffff',
              color: leadForm?.form_config?.text_color || '#111827'
            }}
          >
            <div className={`grid grid-cols-1 ${isEmbed ? '' : 'lg:grid-cols-3'} gap-0`}>
              {/* Main Success Area */}
              <div className={`${isEmbed ? 'p-4' : 'lg:col-span-2 p-12'} text-center`}>
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckSquare className="w-10 h-10 text-green-600" />
                </div>
                <h1 
                  className="text-3xl font-bold mb-4"
                  style={{ color: leadForm?.form_config?.text_color || '#111827' }}
                >
                  Thank You!
                </h1>
                <p 
                  className="text-lg mb-8"
                  style={{ color: leadForm?.form_config?.text_color || '#6b7280' }}
                >
                  Your form has been submitted successfully. We'll get back to you soon.
                </p>
                {!isEmbed ? (
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Close
                </button>
                ) : null}
              </div>

              {/* Contact Information Sidebar */}
              {!isEmbed && (leadForm?.form_config?.show_phone || leadForm?.form_config?.show_email || leadForm?.form_config?.show_address) && (
                <div className="lg:col-span-1 bg-gray-50 p-8 border-l border-gray-200">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Contact Information</h3>
                  <div className="space-y-6">
                    {leadForm?.form_config?.show_phone && business && (
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          <Phone className="w-5 h-5 text-gray-700" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900 mb-1">Phone</p>
                          <p className="text-sm text-gray-600">{business.phone || 'Phone number not available'}</p>
                        </div>
                      </div>
                    )}
                    
                    {leadForm?.form_config?.show_email && business && (
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          <Mail className="w-5 h-5 text-gray-700" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900 mb-1">Email</p>
                          <p className="text-sm text-gray-600">{business.email || 'Email not available'}</p>
                        </div>
                      </div>
                    )}

                    {leadForm?.form_config?.show_address && business && (
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          <MapPin className="w-5 h-5 text-gray-700" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900 mb-1">Studio Location</p>
                          <p className="text-sm text-gray-600">{business.address || 'Address not available'}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Direct Contact Section */}
                  {leadForm?.form_config?.show_direct_contact && (
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Direct Contact</h4>
                      <p className="text-sm text-gray-600 mb-6">
                        Prefer to reach us directly? Feel free to call or email us, and we'll get back to you as soon as possible.
                      </p>
                      
                      <div className="space-y-3">
                        {leadForm?.form_config?.show_phone && business?.phone && (
                          <a
                            href={`tel:${business.phone}`}
                            className="block w-full text-center py-3 px-4 text-white font-medium rounded-md transition-colors"
                            style={{ 
                              backgroundColor: leadForm?.form_config?.button_color || '#2563eb'
                            }}
                            onMouseEnter={(e) => {
                              const bgColor = leadForm?.form_config?.button_color || '#2563eb';
                              e.currentTarget.style.backgroundColor = `${bgColor}dd`;
                            }}
                            onMouseLeave={(e) => {
                              const bgColor = leadForm?.form_config?.button_color || '#2563eb';
                              e.currentTarget.style.backgroundColor = bgColor;
                            }}
                          >
                            Call Now
                          </a>
                        )}
                        
                        {leadForm?.form_config?.show_email && business?.email && (
                          <a
                            href={`mailto:${business.email}`}
                            className="block w-full text-center py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors"
                          >
                            Send Email
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={isEmbed ? 'min-h-0' : 'min-h-screen'}
      style={{ backgroundColor: leadForm?.form_config?.background_color || (isEmbed ? '#ffffff' : '#f9fafb') }}
    >
      {/* Header */}
      {!isEmbed ? (
      <div 
        className="h-24 w-full flex items-center relative px-6 sm:px-8" 
        style={{ backgroundColor: leadForm?.form_config?.header_color || business?.booking_settings?.navbar_color || '#000000' }}
      >
        {/* Logo - Left Side */}
        {business?.booking_settings?.logo_url && (
          <div className="flex-shrink-0">
            <img 
              src={business.booking_settings.logo_url} 
              alt={`${business.name} logo`}
              className="h-16 max-w-xs object-contain"
            />
          </div>
        )}
        
        {/* Business Name - Centered */}
        {leadForm?.form_config?.show_business_name !== false && (
          <div className="absolute inset-0 flex items-center justify-center">
            <h1 
              className="text-3xl font-bold"
              style={{ color: '#ffffff' }}
            >
              {business.name}
            </h1>
          </div>
        )}
      </div>
      ) : null}

      {/* Form */}
      <div className={`${isEmbed ? 'max-w-xl mx-auto px-3 py-4' : 'max-w-6xl mx-auto px-4 py-8'}`}>
        <div 
          className={`${isEmbed ? '' : 'bg-white rounded-lg shadow-md overflow-hidden'}`}
          style={{ 
            backgroundColor: leadForm?.form_config?.background_color || '#ffffff',
            color: leadForm?.form_config?.text_color || '#111827'
          }}
        >
          <div className={`grid grid-cols-1 ${isEmbed ? '' : 'lg:grid-cols-3'} gap-0`}>
            {/* Main Form Area */}
            <div className={`${isEmbed ? 'p-2' : 'lg:col-span-2 p-8'}`}>
              <div className="mb-8">
                <h1 
                  className="text-3xl font-bold mb-3"
                  style={{ color: leadForm?.form_config?.text_color || '#111827' }}
                >
                  {leadForm.title}
                </h1>
                {leadForm.form_config?.sub_header && (
                  <p 
                    className="text-lg mb-3"
                    style={{ color: leadForm?.form_config?.text_color || '#6b7280' }}
                  >
                    {leadForm.form_config.sub_header}
                  </p>
                )}
                {leadForm.description && (
                  <p 
                    className="text-gray-600"
                    style={{ color: leadForm?.form_config?.text_color || '#6b7280' }}
                  >
                    {leadForm.description}
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmit}>
                {leadForm.form_config?.fields?.map(renderField)}

                <div className="mt-12">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 text-white font-semibold text-lg rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    style={{ 
                      backgroundColor: leadForm?.form_config?.button_color || '#2563eb',
                      borderColor: leadForm?.form_config?.button_color || '#2563eb'
                    }}
                    onMouseEnter={(e) => {
                      const bgColor = leadForm?.form_config?.button_color || '#2563eb';
                      e.currentTarget.style.backgroundColor = `${bgColor}dd`;
                    }}
                    onMouseLeave={(e) => {
                      const bgColor = leadForm?.form_config?.button_color || '#2563eb';
                      e.currentTarget.style.backgroundColor = bgColor;
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : (leadForm?.form_config?.button_text || 'Submit Form')}
                  </button>
                </div>
              </form>
            </div>

            {/* Contact Information Sidebar */}
            {!isEmbed && (leadForm?.form_config?.show_phone || leadForm?.form_config?.show_email || leadForm?.form_config?.show_address) && (
              <div className="lg:col-span-1 bg-gray-50 p-8 border-l border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Contact Information</h3>
                <div className="space-y-6">
                  {leadForm?.form_config?.show_phone && business && (
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        <Phone className="w-5 h-5 text-gray-700" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">Phone</p>
                        <p className="text-sm text-gray-600">{business.phone || 'Phone number not available'}</p>
                      </div>
                    </div>
                  )}
                  
                  {leadForm?.form_config?.show_email && business && (
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        <Mail className="w-5 h-5 text-gray-700" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">Email</p>
                        <p className="text-sm text-gray-600">{business.email || 'Email not available'}</p>
                      </div>
                    </div>
                  )}

                  {leadForm?.form_config?.show_address && business && (
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        <MapPin className="w-5 h-5 text-gray-700" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">Studio Location</p>
                        <p className="text-sm text-gray-600">{business.address || 'Address not available'}</p>
                      </div>
                    </div>
                  )}
                </div>

                                   {/* Direct Contact Section */}
                   {leadForm?.form_config?.show_direct_contact && (
                     <div className="mt-8 pt-6 border-t border-gray-200">
                       <h4 className="text-lg font-semibold text-gray-900 mb-3">Direct Contact</h4>
                       <p className="text-sm text-gray-600 mb-6">
                         Prefer to reach us directly? Feel free to call or email us, and we'll get back to you as soon as possible.
                       </p>
                       
                       <div className="space-y-3">
                         {leadForm?.form_config?.show_phone && business?.phone && (
                           <a
                             href={`tel:${business.phone}`}
                             className="block w-full text-center py-3 px-4 text-white font-medium rounded-md transition-colors"
                             style={{ 
                               backgroundColor: leadForm?.form_config?.button_color || '#2563eb'
                             }}
                             onMouseEnter={(e) => {
                               const bgColor = leadForm?.form_config?.button_color || '#2563eb';
                               e.currentTarget.style.backgroundColor = `${bgColor}dd`;
                             }}
                             onMouseLeave={(e) => {
                               const bgColor = leadForm?.form_config?.button_color || '#2563eb';
                               e.currentTarget.style.backgroundColor = bgColor;
                             }}
                           >
                             Call Now
                           </a>
                         )}
                         
                         {leadForm?.form_config?.show_email && business?.email && (
                           <a
                             href={`mailto:${business.email}`}
                             className="block w-full text-center py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors"
                           >
                             Send Email
                           </a>
                         )}
                       </div>
                     </div>
                   )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 